/**
 * What the quiet-path gate has to join (03 §5.2). For a parachutist it is one leg: anywhere in the drop
 * band to the edge of the goal zone. A prisoner (10 §2, mission 32: `startCaptured`) never sees the drop
 * band - his quiet way runs from the cell to his pods, and from the pods to the pickup.
 */

import type { MissionDef, Rect } from '../../../sim/types';

export interface QuietLeg {
  /** Every open cell in here is somewhere he may start the leg. */
  from: Rect;
  to: Rect;
}

/** How close is close enough: within a pod's Interact reach and a little, and standing on the pickup's X. */
const POD_REACH = 2.5;
const PICKUP_REACH = 3;

const around = (p: { x: number; z: number }, r: number): Rect => ({ x0: p.x - r, z0: p.z - r, x1: p.x + r, z1: p.z + r });

/** The legs of a captured start, or null for a mission that begins in the drop band. */
export function capturedLegs(def: MissionDef): QuietLeg[] | null {
  if (def.startCaptured !== true) return null;
  const pod = def.podsAt?.[0];
  // The square metre he stands on, and nothing else: he does not choose where he comes down.
  const [cx, cz] = [Math.floor(def.player.x), Math.floor(def.player.z)];
  const cell: Rect = { x0: cx, z0: cz, x1: cx + 1, z1: cz + 1 };
  if (!pod) return [{ from: cell, to: around(def.pickup, PICKUP_REACH) }];
  return [
    { from: cell, to: around(pod, POD_REACH) },
    { from: around(pod, POD_REACH), to: around(def.pickup, PICKUP_REACH) },
  ];
}
